import { useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { QueryKey } from '@tanstack/query-core';
import { Button } from '@givebutter/primitives/button';
import { faArrowDown, faArrowUp } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { fetchPledge } from 'api/pledges';
import { fetchTicketById } from 'api/tickets';
import { fetchPlanById } from 'api/transactionPlans';
import { fetchTransactionById } from 'api/transactions';
import { ResourceTypeMapping } from 'components/table/types';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { conditionallyFetchOrNull } from 'helpers/reactQuery';
import { last } from 'lodash';
import { accountQueryKeys } from 'queries/account-query-keys';
import { queryClient } from 'queries/queryClient';
import { transactionQueryKeys } from 'queries/transaction-query-keys';
import { IndexedTransaction, Ticket } from 'types/domain';
import { PaginatedData } from 'types/pagination';

type DrawerIncrementorResource = 'transactions' | 'tickets' | 'transaction_plans' | 'pledges';

interface DrawerIncrementorProps {
    resource: DrawerIncrementorResource;
}
const DrawerIncrementor = ({ resource }: DrawerIncrementorProps) => {
    const { accountId } = useCurrentAccount();
    const history = useHistory();
    const { path, queryKey, queryFn, currentId, allIds } = useResourceStrategy(resource);

    const total = allIds.length;
    const currentIndex = allIds.findIndex(id => id === currentId);
    const prevId = allIds[currentIndex - 1];
    const nextId = allIds[currentIndex + 1];

    useEffect(() => {
        queryClient.prefetchQuery({
            queryKey: queryKey(accountId, prevId),
            queryFn: () => conditionallyFetchOrNull(() => queryFn(prevId), !!prevId),
        });
    }, [prevId, accountId, queryKey, queryFn]);

    useEffect(() => {
        queryClient.prefetchQuery({
            queryKey: queryKey(accountId, nextId),
            queryFn: () => conditionallyFetchOrNull(() => queryFn(nextId), !!nextId),
        });
    }, [nextId, accountId, queryKey, queryFn]);

    if (allIds?.length <= 1 || !currentId || currentIndex < 0) {
        return null;
    }

    return (
        <div className="flex flex-shrink-0 items-center gap-2">
            <p className="min-w-0 text-xs">
                {currentIndex + 1} / {total}
            </p>
            <div className="flex items-center gap-1">
                <Button
                    variant="outline"
                    size="sm"
                    color="neutral"
                    shape="square"
                    disabled={!prevId}
                    onClick={() => history.push(hydrateItemPath(path, prevId))}
                    aria-label="Previous"
                >
                    <FontAwesomeIcon icon={faArrowUp} />
                </Button>
                <Button
                    variant="outline"
                    size="sm"
                    color="neutral"
                    shape="square"
                    disabled={!nextId}
                    onClick={() => history.push(hydrateItemPath(path, nextId))}
                    aria-label="Next"
                >
                    <FontAwesomeIcon icon={faArrowDown} />
                </Button>
            </div>
        </div>
    );
};

type ResourceStrategy = {
    currentId: number;
    allIds: number[];
    path: `${string}/:id${string}`;
    queryKey: (accountId: number, itemId: number) => QueryKey;
    queryFn: (id: number) => Promise<unknown>;
};
const useResourceStrategy = (resource: DrawerIncrementorResource): ResourceStrategy => {
    const { accountId } = useCurrentAccount();
    const params = useParams<{
        transaction_id: string;
        ticket_id: string;
        plan_id: string;
        pledge_id: string;
    }>();

    switch (resource) {
        case 'transactions': {
            const queriesData = queryClient.getQueriesData<PaginatedData<IndexedTransaction>>({
                queryKey: transactionQueryKeys.transactions(accountId),
                predicate: queriesDataPredicateFactory(resource),
            });

            return {
                currentId: parseInt(params.transaction_id),
                allIds: transformQueriesDataToIds(queriesData),
                path: `/accounts/${accountId}/transactions/:id`,
                queryKey: transactionQueryKeys.transaction,
                queryFn: (id: number) => fetchTransactionById(id),
            };
        }
        case 'tickets': {
            const queriesData = queryClient.getQueriesData<PaginatedData<Ticket>>({
                queryKey: accountQueryKeys.tickets(accountId),
                predicate: queriesDataPredicateFactory(resource),
            });

            return {
                currentId: parseInt(params.ticket_id),
                allIds: transformQueriesDataToIds(queriesData),
                path: `/accounts/${accountId}/tickets/:id`,
                queryKey: accountQueryKeys.ticketById,
                queryFn: (id: number) => fetchTicketById(id),
            };
        }
        case 'transaction_plans': {
            const queriesData = queryClient.getQueriesData<PaginatedData<Ticket>>({
                queryKey: accountQueryKeys.plans(accountId),
                predicate: queriesDataPredicateFactory(resource),
            });

            return {
                currentId: parseInt(params.plan_id),
                allIds: transformQueriesDataToIds(queriesData),
                path: `/accounts/${accountId}/plans/:id`,
                queryKey: accountQueryKeys.planById,
                queryFn: (id: number) => fetchPlanById(accountId, id),
            };
        }
        case 'pledges': {
            const queriesData = queryClient.getQueriesData<PaginatedData<Ticket>>({
                queryKey: accountQueryKeys.pledges(accountId),
                predicate: queriesDataPredicateFactory(resource),
            });
            return {
                currentId: parseInt(params.pledge_id),
                allIds: transformQueriesDataToIds(queriesData),
                path: `/accounts/${accountId}/pledges/:id/edit`,
                queryKey: accountQueryKeys.pledgeById,
                queryFn: (id: number) => fetchPledge(accountId, id),
            };
        }
    }
};

const queriesDataPredicateFactory =
    (resource: DrawerIncrementorResource) =>
    ({ queryKey }: { queryKey: QueryKey }) => {
        if (
            queryKey[0] === 'account' &&
            typeof queryKey[1] === 'number' &&
            queryKey[2] === resourceToResourceKey(resource) &&
            typeof queryKey[3] === 'object'
        ) {
            return true;
        }
        return false;
    };

const transformQueriesDataToIds = (
    queriesData: [
        QueryKey,
        PaginatedData<ResourceTypeMapping[DrawerIncrementorResource]> | undefined,
    ][],
) => {
    const lastQuery = last(queriesData);

    if (!lastQuery) {
        return [];
    }

    const [_, lastQueryData] = lastQuery;

    return lastQueryData?.data.map(item => item.id) ?? [];
};

const hydrateItemPath = (itemPath: string, id: number) => {
    return itemPath.replace(':id', id.toString());
};

const resourceToResourceKey = (resource: DrawerIncrementorResource) => {
    if (resource === 'transaction_plans') {
        return 'plans';
    }
    return resource;
};

export { DrawerIncrementor };
